import { CreditCard } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { adminApi } from "@/client/services/admin";

const PAGE_SIZE = 20;

// Helper functions for Badge variants
const getPaymentStatusVariant = (status?: string) => {
  switch (status) {
    case "SUCCESSFUL": {
      return "success";
    }
    case "PENDING": {
      return "warning";
    }
    default: {
      return "error";
    }
  }
};

export const AdminPaymentsPage = () => {
  const [status, setStatus] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [page, setPage] = useState(0);

  const offset = page * PAGE_SIZE;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-payments", status, dateFrom, dateTo, offset],
    queryFn: () =>
      adminApi.getPayments({
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const payments = data?.payments ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Admin Payments - VillageHut Resume</title>
      </Helmet>

      <h1 className="text-2xl font-semibold">Payments</h1>

      <div className="flex flex-wrap items-center gap-4">
        <Select
          value={status || "all"}
          onValueChange={(v) => {
            setStatus(v === "all" ? "" : v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="PENDING">PENDING</SelectItem>
            <SelectItem value="SUCCESSFUL">SUCCESSFUL</SelectItem>
            <SelectItem value="FAILED">FAILED</SelectItem>
            <SelectItem value="CANCELLED">CANCELLED</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="date"
          placeholder="From"
          className="w-[160px]"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(0);
          }}
        />
        <Input
          type="date"
          placeholder="To"
          className="w-[160px]"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(0);
          }}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-5" />
            Payment list
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-muted-foreground">Loading...</p>}
          {error && <p className="text-destructive">Failed to load payments.</p>}

          {!isLoading && !error && payments.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">No payments found.</div>
          )}

          {!isLoading && !error && payments.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payments ?? [])
                      .filter((p: unknown): p is Record<string, unknown> => p != null)
                      .map(
                        (p: {
                          id?: string | number;
                          createdAt?: string;
                          user?: { email?: string };
                          amount?: number | string;
                          status?: string;
                          paymentType?: string;
                          phoneNumber?: string;
                          subscription?: { planName?: string };
                        }) => (
                          <tr key={String(p.id)} className="border-b">
                            <td className="px-4 py-3 text-sm">
                              {dayjs(String(p.createdAt ?? "")).format("MMM D, YYYY HH:mm")}
                            </td>
                            <td className="px-4 py-3 text-sm">{p.user?.email ?? "—"}</td>
                            <td className="px-4 py-3 text-sm font-medium">
                              KES {Number(p.amount ?? 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={getPaymentStatusVariant(p.status ?? "")}>
                                {String(p.status ?? "—")}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">{p.paymentType ?? "—"}</td>
                            <td className="px-4 py-3 text-sm">{p.phoneNumber ?? "—"}</td>
                            <td className="px-4 py-3 text-sm">{p.subscription?.planName ?? "—"}</td>
                          </tr>
                        ),
                      )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => {
                        setPage((p) => p - 1);
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => {
                        setPage((p) => p + 1);
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
